import { getLocalTimeZone, today } from '@internationalized/date';
import Modal from '@orn/shared-web/components/Modal';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { useOldestTransactionDate } from '../../providers/TransactionsProvider';

import { createPresets } from './DateRangePicker';

import type { CalendarDateRange, PresetDateRangeKey } from './DateRangePicker';

const PRESET_KEYS: PresetDateRangeKey[] = [
  'thisMonth',
  'pastMonth',
  'past3Months',
  'past6Months',
  'thisYear',
  'pastYear',
  'allTime',
];

interface IDateRangePickerModalProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  value?: CalendarDateRange;
  defaultValue?: PresetDateRangeKey;
  onChange?: (range: CalendarDateRange) => void;
}

const DateRangePickerModal = ({
  isOpen,
  onOpenChange,
  value,
  defaultValue = 'past6Months',
  onChange,
}: IDateRangePickerModalProps) => {
  const now = React.useMemo(() => today(getLocalTimeZone()), []);
  const oldestTransactionDate = useOldestTransactionDate();

  const presets = React.useMemo(() => {
    const defs = createPresets(now, oldestTransactionDate);
    return PRESET_KEYS.map((key) => ({
      key,
      label: defs[key].label,
      value: defs[key].getValue(),
    }));
  }, [now, oldestTransactionDate]);

  const defaultRange = React.useMemo(
    () => createPresets(now, oldestTransactionDate)[defaultValue].getValue(),
    [now, oldestTransactionDate, defaultValue]
  );

  const effectiveValue = value ?? defaultRange;

  const selectedKey = React.useMemo(() => {
    for (const { key, value: presetValue } of presets) {
      if (effectiveValue.start.compare(presetValue.start) === 0 && effectiveValue.end.compare(presetValue.end) === 0) {
        return key;
      }
    }
    return null;
  }, [presets, effectiveValue]);

  const handlePresetClick = (range: CalendarDateRange) => {
    onChange?.(range);
    onOpenChange?.(false);
  };

  const handleClose = () => {
    onOpenChange?.(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="xs">
      <Modal.Header onClose={handleClose}>Time range</Modal.Header>
      <Modal.Body className="w-full items-stretch p-0 pb-6">
        <div className="flex flex-col gap-0.5 px-2">
          {presets.map(({ key, label, value: presetValue }) => {
            const isSelected = selectedKey === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handlePresetClick(presetValue)}
                className={cx(
                  'outline-focus-ring flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-left text-sm font-medium transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2',
                  isSelected
                    ? 'bg-active text-secondary_hover hover:bg-secondary_hover'
                    : 'text-secondary hover:bg-primary_hover hover:text-secondary_hover'
                )}
              >
                <span>{label}</span>
                {isSelected && (
                  <span className="text-brand-solid shrink-0 text-base" aria-hidden>
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DateRangePickerModal;
